import { Component } from '@angular/core';
import { IonicPage, NavController, NavParams, AlertController, ModalController, ToastController, LoadingController } from 'ionic-angular';
import 'rxjs/add/operator/map';
import { map } from 'rxjs/operators/map';
import * as moment from "moment";
import _ from 'lodash';
import { AuthProvider } from '../../providers/auth/auth';
import { DatabaseProvider } from '../../providers/database/database';
import { UtilityProvider } from '../../providers/utility/utility';
import { PatientAddEditPage } from '../patient-add-edit/patient-add-edit';
import { AdherenceCallDialogComponent } from '../../components/adherence-call-dialog/adherence-call-dialog';
import { MatDialog, MatDialogRef } from '@angular/material';
import { PatientProvider } from '../../providers/patient/patient';
import { ChartDialogComponent } from '../../components/chart-dialog/chart-dialog';
import { StatusDialogComponent } from '../../components/status-dialog/status-dialog';
import { StatusTherapyDialogComponent } from '../../components/status-therapy-dialog/status-therapy-dialog';
import { Subscription, Subject } from 'rxjs';
import { ClinicProvider } from '../../providers/clinic/clinic';

@IonicPage()
@Component({
  selector: 'page-patient-details',
  templateUrl: 'patient-details.html',
})

export class PatientDetailsPage {
  private ngUnsubscribe: Subject<any> = new Subject();
  program_name: string;
  program_id: string;

  patient_id;
  patient: any;
  patientSub: Subscription;

  user: any;
  userid: string;
  user_fullname: string;
  user_saving: any;

  selectedTabIndex = 0;
  orderPanelStatus = false;
  reimbursementPanelStatus = false;
  aePanelStatus = false

  fullname: string = null;
  prescriptions: any[] = [];
  therapies: any[] = [];
  dosages: any[] = [];
  pharmacies: any[] = [];

  clinic: any;
  statuses: any;
  substatuses: any;
  indications: any;

  status_new: string;
  substatus_new: string;
  status_note: string;

  adherenceCallDialogRef: MatDialogRef<AdherenceCallDialogComponent>;
  chartDialogRef: MatDialogRef<ChartDialogComponent>;
  statusDialogRef: MatDialogRef<StatusDialogComponent>;
  statusTherapyDialogRef: MatDialogRef<StatusTherapyDialogComponent>;

  constructor(
    public navCtrl: NavController, 
    public navParams: NavParams, 
    public alertCtrl: AlertController, 
    public toastCtrl: ToastController,
    public loadingCtrl: LoadingController,
    private database: DatabaseProvider,
    private auth: AuthProvider,
    public modalCtrl: ModalController,
    private utility: UtilityProvider,
    private matDialog: MatDialog,
    private dbPatient: PatientProvider,
    private dbClinic: ClinicProvider
  ) {
    this.selectedTabIndex = 0;
   }

   async ionViewDidEnter()  {
    await this.getUserProgram();
    this.init();    
   }

   async init()  {
    const loader = this.loadingCtrl.create({});
    loader.present().then(async () => {
      await this.getPatient();
      await this.getTherapyDoses();      
      loader.dismiss();
    });
  }

  ngOnDestroy() {
    console.log('Leaving patient detail view and destroying subscriptions');
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  async getUserProgram()  {
    return new Promise((resolve) => {
      this.auth.getCurrentUser().then(async user => {
        this.user = user;
        this.userid = user.id;
        this.fullname = user.first_name + ' ' + user.last_name;
        this.user_fullname = user.first_name + ' ' + user.last_name;
        this.user_saving = { id: user.id, name: user.first_name + ' ' + user.last_name };
        this.program_name = user.program_name;
        this.program_id = user.program_id;
        resolve();
      });
    });
  }

  async getPatient()  {
    this.patient_id = this.navParams.get('patient_id');
    return new Promise((resolve) => {
      this.patientSub = this.dbPatient
      .getPatient(this.program_id, this.patient_id)
      .snapshotChanges()
      .pipe(map(changes => {
        const data: any = changes.payload.data();
        const id = changes.payload.id;
        return { id, ...data };
      }))
      .takeUntil(this.ngUnsubscribe)
      .subscribe((patient: any) => {
        console.log('Patient: ', patient);
        patient['drugtype'] = this.utility.getDrugType(patient.reimbursement_private, patient.reimbursement_private_other, patient.reimbursement_public, patient.reimbursement_compassionate);
        patient['reimbursementtype'] = this.utility.getReimbursementType(patient.reimbursement_private, patient.reimbursement_private_other, patient.reimbursement_public, patient.reimbursement_compassionate, patient.reimbursement_fa);
        patient['reimbursementstatus'] = this.utility.getReimbursementActiveType(patient.reimbursement_private, patient.reimbursement_private_other, patient.reimbursement_public);
        this.patient = patient;
        resolve();
      });  
    });
  }

  async getTherapyDoses() {
    return new Promise((resolve) => {
      this.database
      .getTherapyByProgram(this.program_id)
      .snapshotChanges()
      .map(actions => {
        return actions.map(a => {
          const data = a.payload.doc.data() as any;
          data.id = a.payload.doc.id;
          return data;
        });
      })
      .takeUntil(this.ngUnsubscribe)
      .subscribe((therapies) => {
        this.therapies = therapies;
        if (this.patient.therapy) {
          this.dosages = _.filter(this.therapies, therapy => therapy.name === this.patient.therapy)[0]['dosing'];
        }
        resolve();
      });
    });
  }

  tabChange(event) {
    Promise.resolve().then(() => {
      setTimeout(() => {
        this.selectedTabIndex = event.index
      },100);
    });
  }

  isDisabled()  {
    if (this.patient.therapy && this.patient.indication && this.patient.doctor && this.patient.address && this.patient.city && this.patient.province && this.patient.postal_code) {
      return false;
    } else {
      return true;
    }
  }

  editPatient()  {
    this.navCtrl.push(PatientAddEditPage, { 'patient_id': this.patient.id });
  }

  openAdherenceCallDialog() {
    this.adherenceCallDialogRef = this.matDialog.open(AdherenceCallDialogComponent, {
      hasBackdrop: true,
      disableClose: true,
      width: '400px',
      data: { patient: this.patient, doses: this.dosages }
    });

    this.adherenceCallDialogRef
    .afterClosed()
    .takeUntil(this.ngUnsubscribe)
    .subscribe(result => {
      if(result) {
        const patient = {
          id: this.patient.id,
          first_name: this.patient.first_name,
          last_name: this.patient.last_name,
          program_patient_id: this.patient.program_patient_id
        }
        const adherencecall = { patient, ...result };
        this.dbPatient.addAdherenceCall(this.program_id, this.patient_id, adherencecall, this.user_saving)
        .then(() => {
          this.utility.presentToast('Saved Adherence Call');
        })
        .catch(err => {
          this.utility.presentToast('Error saving adherence call');
        });
     }
    });
  }

  openChartDialog() {
    this.chartDialogRef = this.matDialog.open(ChartDialogComponent, {
      hasBackdrop: true,
      disableClose: true,
      width: '400px',
      data: { patient: this.patient, program_id: this.program_id }
    });

    this.chartDialogRef
    .afterClosed()
    .takeUntil(this.ngUnsubscribe)
    .subscribe(result => {
      if(result) {
        const patient = {
          id: this.patient.id,
          first_name: this.patient.first_name,
          last_name: this.patient.last_name,
          program_patient_id: this.patient.program_patient_id
        }
        const chart = { patient, ...result };
        this.dbPatient.addChart(this.program_id, this.patient.id, chart, this.user_saving)
        .then(() => {
          this.utility.presentToast('Saved chart');
        })
        .catch(err => {
          this.utility.presentToast('Error saving chart');
        });
     }
    });
  }

  openStatusDialog() {
    this.statusDialogRef = this.matDialog.open(StatusDialogComponent, {
      hasBackdrop: true,
      disableClose: true,
      width: '400px',
      data: { patient: this.patient, program_id: this.program_id }
    });

    this.statusDialogRef
    .afterClosed()
    .takeUntil(this.ngUnsubscribe)
    .subscribe(result => {
      if(result) {
        const patient = {
          id: this.patient.id,
          first_name: this.patient.first_name,
          last_name: this.patient.last_name,
          program_patient_id: this.patient.program_patient_id
        }
        if (!result.substatus) { result.substatus = null; }
        if (!result.therapy_status) { result.therapy_status = null; }
        if (!result.therapy_substatus) { result.therapy_substatus = null; }
        if (!result.therapy_type) { result.therapy_type = null; }
        const status = { patient, ...result };
        this.dbPatient.updateStatus(this.program_id, this.patient.id, this.patient.status, status, this.user_saving)
        .then(() => {
          this.utility.presentToast('Saved status update');
        })
        .catch(err => {
          this.utility.presentToast('Error saving status update');
        });
     }
    });
  }

  openTherapyDialog() {
    this.statusTherapyDialogRef = this.matDialog.open(StatusTherapyDialogComponent, {
      hasBackdrop: true,
      disableClose: true,
      width: '400px',
      data: { patient: this.patient, program_id: this.program_id }
    });

    this.statusTherapyDialogRef
    .afterClosed()
    .takeUntil(this.ngUnsubscribe)
    .subscribe(result => {
      if(result) {
        const patient = {
          id: this.patient.id,
          first_name: this.patient.first_name,
          last_name: this.patient.last_name,
          program_patient_id: this.patient.program_patient_id
        }
        if (!result.therapy_status) { result.therapy_status = null; }
        if (!result.therapy_substatus) { result.therapy_substatus = null; }
        if (!result.therapy_type) { result.therapy_type = null; }
        const therapy = { patient, ...result };
        this.dbPatient.updateTherapy(this.program_id, this.patient.id, therapy, this.user_saving)
        .then(() => {
          this.utility.presentToast('Saved therapy update');
        })
        .catch(err => {
          this.utility.presentToast('Error saving therapy update');
        });
     }
    });
  }


  deletePatient(item){
    let confirm = this.alertCtrl.create({
      title: "Delete Dispensing",
      message: "Are you sure you want to delete this dispensing entry?  If so, type 'delete' into the textbox to confirm",
      inputs: [
        {
          name: 'delete_text',
          placeholder: 'delete',
          type: 'text'
        }
      ],
      buttons: [
        {
          text: "Cancel",
          handler: () => {
            // console.log("Don't update");
          }
        },
        {
          text: "Delete Dispensing",
          handler: data => {
            let delete_text = data.delete_text;
            if(delete_text === 'delete') {
              this.database.deletePatient(this.program_id, this.patient.id).then((val) => {
                this.utility.presentToast('The patient file was successfully deleted');
                this.navCtrl.pop();
              })
              .catch((err) => {
                this.utility.presentToast('The patient file could not be deleted');
              })
            } else {
              return;
            }
          }
        }
      ]
    });
    confirm.present();
    }

  formatDate(val, format) {
    if (format) {
      return this.utility.formatDate(val, format);
    } else {
      return this.utility.formatDate(val, 'YYYY-MMM-DD');
    }
  }

  // ionViewCanLeave() {
  //   // here you can use other vars to see if there are reasons we want to keep user in this page:
  //         return new Promise((resolve, reject) => {
  //           let alert = this.alertCtrl.create({
  //             title: 'Are you sure?',
  //             message: 'Please ensure everything is completed before leaving the patient file. If you leave you must complete a chart entry',
  //             buttons: [
  //               {
  //                 text: 'Stay',
  //                 role: 'cancel',
  //                 handler: () => {
  //                   console.log('User stayed');
  //                   reject();
  //                 }
  //               },
  //               {
  //                 text: 'Leave, Add Chart',
  //                 handler: () => {
  //                   console.log('User leaves');
  //                   resolve();
  //                 }
  //               }
  //             ]
  //           });
  //           alert.present();
  //         });
  //     }

  ionViewWillLeave() {
    setTimeout(() => {
      if(this.patientSub) {
        this.patientSub.unsubscribe();
      }
    }, 0);
  }
}



// WEBPACK FOOTER //
// ./src/pages/patient-details/patient-details.ts